import { getApi, postApi, putApi } from "../helpers/auth_helpers";

const base = process.env.REACT_APP_SERVERURL || "";

export type ApprovalShow = { price: boolean; advance: boolean; images: boolean; specs: boolean };

export type ApprovalStatus =
  | "PENDING"
  | "APPROVED"
  | "CHANGES_REQUESTED"
  | "EXPIRED"
  | "REVOKED"
  | "SUPERSEDED";

export type ApprovalReminder = { at: string; channel: string; by?: string; note?: string };

/** A change to the order after the customer answered; their answer stays as it was. */
export type ApprovalOrderChange = {
  at: string;
  kind: "EDITED" | "CANCELLED" | "REMOVED";
  fields?: string[];
  byName?: string | null;
};

export type ApprovalKept = { status: "NOT_NEEDED"; note?: string | null; at: string; byName?: string | null };

/** A file from the order shared on the approval link; the customer opens it through a short-lived URL. */
export type ApprovalFile = { id: string; name?: string | null; contentType?: string | null; size?: number | null };

/** The customer's choice on the approval page, recorded with their answer. */
/** true agreed, false said no, null/missing no answer (updates continue). */
export type ApprovalConsent = { whatsapp?: boolean | null; email?: boolean | null; at?: string | null };

export type AdvanceStatus = "DUE" | "CUSTOMER_SAYS_PAID" | "RECEIVED";

/** The order's advance as the studio records it (order view). */
export type OrderAdvancePayment = {
  status: AdvanceStatus;
  amount?: number | null;
  receivedAt?: string | null;
  receivedByName?: string | null;
  note?: string | null;
  customerPaidAt?: string | null;
  customerReference?: string | null;
};

export type MarkAdvanceBody = { received: boolean; amount?: number; receivedOn?: string; note?: string };

/** Where the customer pays the advance; set by the studio in approval settings. */
export type PaymentDetails = {
  upiId?: string | null;
  payeeName?: string | null;
  bankName?: string | null;
  accountName?: string | null;
  accountNumber?: string | null;
  ifsc?: string | null;
  note?: string | null;
};

export type StudioApproval = {
  approvalId?: string;
  orderId?: string;
  orderCode?: string;
  status: ApprovalStatus | null;
  url?: string | null;
  language?: string;
  show?: ApprovalShow;
  createdAt?: string;
  createdByName?: string;
  expiresAt?: string;
  views?: number;
  firstViewedAt?: string;
  lastViewedAt?: string;
  decidedAt?: string;
  decidedByName?: string;
  decisionNote?: string;
  reminders?: ApprovalReminder[];
  endedReason?: string;
  warnings?: string[];
  changesAfterDecision?: ApprovalOrderChange[];
  reapproval?: ApprovalKept | null;
  /** Order files shared on this link. */
  files?: ApprovalFile[];
  /** What the customer agreed to be messaged on, recorded with their answer. */
  consent?: ApprovalConsent | null;
  /** Approved, then the order changed: the studio asks again or records that no new approval is needed. */
  needsReapprovalDecision?: boolean;
  /** The order's earlier links, newest first; an answered one keeps its answer and the changes after it. */
  history?: Array<{
    approvalId: string;
    status: ApprovalStatus;
    createdAt?: string;
    decidedAt?: string;
    decidedByName?: string | null;
    decisionNote?: string | null;
    changesAfterDecision?: ApprovalOrderChange[];
    reapproval?: ApprovalKept | null;
  }>;
};

export type CreateApprovalBody = {
  show: ApprovalShow;
  language: "en" | "hi";
  expiresInDays?: number;
  customerTitle?: string;
  preOrderNote?: string;
  /** Same key for a retried or double-tapped create, so the server returns the link it already made. */
  idempotencyKey?: string;
  /** Order files the customer may open from the page. */
  fileIds?: string[];
};

export type ApprovalSettings = {
  webhookUrl?: string | null;
  hasWebhookSecret?: boolean;
  webhookSecret?: string;
  defaultExpiryDays?: number;
  maxExpiryDays?: number;
  preOrderNote?: string | null;
  policyLines?: string[];
  paymentDetails?: PaymentDetails | null;
};

export const getOrderApproval = (orderId: string) =>
  getApi(`${base}/studio/orders/${encodeURIComponent(orderId)}/approval`);

export const createOrderApproval = (orderId: string, body: CreateApprovalBody) =>
  postApi(`${base}/studio/orders/${encodeURIComponent(orderId)}/approval`, body);

export const revokeOrderApproval = (approvalId: string, reason: string) =>
  postApi(`${base}/studio/order-approvals/${encodeURIComponent(approvalId)}/revoke`, { reason });

export const keepOrderApprovalAfterChange = (approvalId: string, note?: string) =>
  postApi(`${base}/studio/order-approvals/${encodeURIComponent(approvalId)}/no-reapproval`, { note });

export const logOrderApprovalReminder = (approvalId: string, channel: string, note?: string) =>
  postApi(`${base}/studio/order-approvals/${encodeURIComponent(approvalId)}/reminders`, { channel, note });

/** Record the advance as received (or undo it); answers with the order view. */
export const markOrderAdvance = (orderId: string, body: MarkAdvanceBody) =>
  postApi(`${base}/studio/orders/${encodeURIComponent(orderId)}/advance`, body);

export const getOrderApprovalSettings = () => getApi(`${base}/studio/order-approvals/settings`);

export const saveOrderApprovalSettings = (body: Record<string, unknown>) =>
  putApi(`${base}/studio/order-approvals/settings`, body);

// ---- Public page (no login; the token is the key) ----

export type PublicApprovalOrder = {
  orderCode?: string;
  customerName?: string;
  customerTitle?: string | null;
  city?: string | null;
  productName?: string | null;
  productDesign?: string | null;
  colour?: string | null;
  quantity?: string | null;
  lines?: Array<{ colour?: string | null; quantity?: string | null }>;
  neededBy?: string | null;
  preOrderNote?: string | null;
  policyLines?: string[];
  currency?: string;
  unitPrice?: string | null;
  total?: string | null;
  advance?: string | null;
  balance?: string | null;
  specifications?: string | null;
  images?: string[];
  files?: ApprovalFile[];
};

export type PublicAdvance = {
  amount: number;
  currency?: string;
  status: AdvanceStatus;
  customerPaidAt?: string | null;
  customerReference?: string | null;
  /** Only while the advance is not yet received. */
  payment?: (PaymentDetails & { upiLink?: string | null }) | null;
};

export type PublicApproval = {
  state: ApprovalStatus;
  studio?: { name?: string | null; logoUrl?: string | null };
  orderCode?: string;
  language?: "en" | "hi";
  order?: PublicApprovalOrder;
  expiresAt?: string;
  formToken?: string;
  decision?: { decidedAt?: string; decidedByName?: string; note?: string | null };
  advance?: PublicAdvance | null;
};

export type PublicResult = { ok: true; data: PublicApproval } | { ok: false; status: number; message: string };

const publicPost = async <T>(
  path: string,
  body: Record<string, unknown>
): Promise<{ ok: true; data: T } | { ok: false; status: number; message: string }> => {
  try {
    const response = await fetch(`${base}/public/order-approvals/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      referrerPolicy: "no-referrer",
      cache: "no-store",
    });
    const json = await response.json().catch(() => null);
    if (response.ok && json?.success && json?.data) {
      return { ok: true, data: json.data as T };
    }
    return { ok: false, status: response.status, message: json?.message || "" };
  } catch {
    return { ok: false, status: 0, message: "" };
  }
};

const publicCall = (path: string, body: Record<string, unknown>): Promise<PublicResult> => publicPost<PublicApproval>(path, body);

export type Consent = { consentWhatsapp?: boolean; consentEmail?: boolean };

// The token goes in the body, never the URL path, so server request logs don't hold live links.
export const viewApproval = (token: string) => publicCall("view", { token });

export const approveOrder = (token: string, body: { name: string; checked: boolean; formToken: string } & Partial<Consent>) =>
  publicCall("approve", { token, ...body });

export const requestOrderChange = (
  token: string,
  body: { note: string; name?: string; formToken: string } & Partial<Consent>
) => publicCall("request-change", { token, ...body });

/** The customer says they paid the advance; the studio still confirms it. */
export const markAdvancePaid = (token: string, body: { formToken: string; reference?: string }) =>
  publicCall("advance-paid", { token, ...body });

/** A short-lived URL for one shared file; asked for on tap, never ahead. */
export const openApprovalFile = (token: string, fileId: string) =>
  publicPost<{ url: string; expiresAt?: string }>("file", { token, fileId });
